/**
 * Structured data — the same facts, in the shape a crawler parses.
 * @file src/web/shared/structured-data.ts
 *
 * ── Why build it rather than write it ─────────────────────────────────────
 * Schema.org markup is a second copy of things the site already states: the
 * price, the unit, the product's own name. Written out by hand in a page's
 * JSX it is a copy that nobody re-reads, and the failure mode is specific and
 * bad — a firm sees one number in a search result and another on the page. So
 * the offer is derived from PRICE, the same constant the pricing panel
 * renders, and there is nothing to keep in step.
 *
 * ── Only claims we would make in prose ────────────────────────────────────
 * No aggregateRating, no review markup, no "ratingCount". Google's guidelines
 * refuse self-serving review data, and more to the point we have no ratings;
 * inventing the field to win a star in a result is the one thing in this file
 * that could get the whole site's rich results turned off.
 *
 * @llm-rule WHEN: A marketing page states prices, or sits under a section
 * @llm-rule AVOID: aggregateRating / Review markup written by us
 * @llm-rule AVOID: Repeating these objects inline in a page — build them here
 */

import { brand } from './brand';
import { PRICE } from './pricing';
import { SITE_ORIGIN } from './hooks/useSEO';

/** '$29' → '29'. Schema wants the number, and the currency separately. */
function amount(price: string): string {
  return price.replace(/[^0-9.]/g, '');
}

/**
 * The product itself, with one Offer, because there is one plan.
 *
 * `SoftwareApplication` rather than `Product` because that is what it is, and
 * because the BusinessApplication category is what makes the price band
 * eligible to show at all.
 *
 * The UNIT is the important part of this markup now. A crawler reading only
 * the number would put our 29 beside a competitor's 59 without knowing
 * whether either is per user, per firm or per month — so the offer says per
 * user and per month explicitly, in the description and in the
 * specification.
 */
export function productSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': `${SITE_ORIGIN}/#software`,
    name: brand.name,
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Legal client intake software',
    operatingSystem: 'Web browser',
    url: `${SITE_ORIGIN}/`,
    description:
      'Client intake software for law firms: multilingual forms that save as the client types, drop-off detection, automatic follow-up, e-signature and document collection, with the matter handed to the case system the firm already uses.',
    publisher: { '@id': `${SITE_ORIGIN}/#organization` },
    offers: {
      '@type': 'Offer',
      name: 'Naumene',
      price: amount(PRICE.amount),
      priceCurrency: 'USD',
      url: `${SITE_ORIGIN}/pricing`,
      availability: 'https://schema.org/InStock',
      description: `${PRICE.full}. ${PRICE.includes}`,
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: amount(PRICE.amount),
        priceCurrency: 'USD',
        referenceQuantity: {
          '@type': 'QuantitativeValue',
          value: 1,
          unitCode: 'C62',
          unitText: 'user',
        },
        billingIncrement: 1,
        unitCode: 'MON',
      },
    },
  };
}

/**
 * The trail to a page that sits under a section.
 *
 * Worth the markup only where the hierarchy is real — /compare/<competitor>
 * genuinely sits under /compare — and it is what turns the green URL line in
 * a result from "naumene.com › compare › clio-grow" into readable names.
 */
export function breadcrumbSchema(trail: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((step, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: step.name,
      item: `${SITE_ORIGIN}${step.path}`,
    })),
  };
}
